import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLogin } from "@/hooks/useMe";
import { loginInputSchema, type LoginInput } from "@/schemas";
import { currentBrand } from "@/lib/brand";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const brand = currentBrand();

  const from =
    (location.state as { from?: string } | null)?.from ?? "/dashboard/app";

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginInputSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      navigate(from, { replace: true });
    } catch {
      form.setError("password", { message: "Invalid email or password." });
    }
  });

  return (
    <section className="container max-w-md py-16">
      <h1 className="text-2xl font-semibold">Log in to {brand.name}</h1>
      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium">Email</label>
          <input
            type="email"
            autoFocus
            className="w-full rounded-md border bg-background px-3 py-2"
            {...form.register("email")}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Password</label>
          <input
            type="password"
            className="w-full rounded-md border bg-background px-3 py-2"
            {...form.register("password")}
          />
          {form.formState.errors.password && (
            <p className="mt-1 text-xs text-destructive">
              {form.formState.errors.password.message}
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={login.isPending}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {login.isPending ? "Logging in…" : "Log in"}
        </button>
      </form>
      <p className="mt-4 text-sm text-muted-foreground">
        New here?{" "}
        <Link to="/dashboard/signup" className="text-foreground underline">
          Create an account
        </Link>
      </p>
    </section>
  );
}
